import React, { useState, useEffect } from 'react';
import './App.css';
import API_BASE_URL from './config/api';
import { FaChevronLeft, FaChevronRight, FaUserCircle } from 'react-icons/fa';
import AdminsTable from './components/Admins/AdminsTable';
import AdminForm from './components/Admins/AdminForm';
import StudentsTable from './components/Students/StudentsTable';
import StudentForm from './components/Students/StudentForm';
import FeesTable from './components/Fees/FeesTable';
import FeeForm from './components/Fees/FeeForm';
import PaymentsTable from './components/Payments/PaymentsTable';
import PaymentForm from './components/Payments/PaymentForm';
import AttendanceTable from './components/Attendance/AttendanceTable';
import AttendanceForm from './components/Attendance/AttendanceForm';
import ProfileForm from './components/Profile/ProfileForm';
import ChangePasswordForm from './components/Profile/ChangePasswordForm';
import LogoManager from './components/Logo/LogoManager';
import Pagination from './components/Pagination';

// Get dashboard items based on user role
const getDashboardItems = (adminRole) => {
  const allItems = [
  { label: 'Dashboard', icon: '📊' },
  { label: 'Admins', icon: '🛡️' },
  { label: 'Students', icon: '👨‍🎓' },
  { label: 'Fees', icon: '💰' },
  { label: 'Payments', icon: '🧾' },
    { label: 'Attendance', icon: '📋' },
    { label: 'Logo Settings', icon: '🖼️' },
    { label: 'My Profile', icon: '👤' },
  ];

  switch (adminRole) {
    case 'Super Admin':
      return allItems; // Show all sections
    case 'Director':
      return allItems; // Show all sections
    case 'Accountant':
      return allItems.filter(item => 
        ['Dashboard', 'Students', 'Fees', 'Payments', 'My Profile'].includes(item.label)
      );
    case 'Teacher':
      return allItems.filter(item => 
        ['Dashboard', 'Students', 'Attendance', 'My Profile'].includes(item.label)
      );
    default:
      return [allItems[0]]; // Only Dashboard for unknown roles
  }
};

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [admin, setAdmin] = useState(() => {
    const saved = localStorage.getItem('admin');
    return saved ? JSON.parse(saved) : null;
  });
  const [active, setActive] = useState('Dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);

  // Admins state
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [adminPagination, setAdminPagination] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // admin id
  const [form, setForm] = useState({ name: '', email: '', password: '', mobile: '', role: '' });
  const [formError, setFormError] = useState('');
  const [formTouched, setFormTouched] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [adminSearch, setAdminSearch] = useState('');
  const [adminRoleFilter, setAdminRoleFilter] = useState('');

  // Profile state
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', mobile: '' });
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Logo state
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoError, setLogoError] = useState('');

  // Update browser tab favicon with provided url (absolute)
  const updateFavicon = (absoluteUrl) => {
    try {
      const link = document.querySelector('link[rel="icon"]') || document.createElement('link');
      link.rel = 'icon';
      link.href = absoluteUrl;
      if (!link.parentNode) {
        document.head.appendChild(link);
      }
    } catch (_) {
      // no-op
    }
  };

  // Dashboard state
  const [dashboardStats, setDashboardStats] = useState({ students: 0, admins: 0, feesCollected: 0, recentStudents: [], todayAttendance: 0, pendingFees: 0, totalFees: 0 });
  useEffect(() => {
    if (admin && active === 'Dashboard') {
      fetchDashboardStats();
    }
    // eslint-disable-next-line
  }, [admin, active]);

  // Fetch logo on app load
  useEffect(() => {
    if (admin) {
      fetchCurrentLogo();
    }
    // eslint-disable-next-line
  }, [admin]);
  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/dashboard/stats`, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to load dashboard stats');
      }
      setDashboardStats(data);
    } catch (e) {
      if (e.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setDashboardStats({ students: 0, admins: 0, feesCollected: 0, recentStudents: [], todayAttendance: 0, pendingFees: 0, totalFees: 0 });
    }
  };

  const roleOptions = [
    'Super Admin',
    'Director',
    'Accountant',
    'Teacher',
  ];

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const isSuperAdmin = admin && admin?.role === 'Super Admin';
  const isDirector = admin && admin?.role === 'Director';
  const isSuperAdminOrDirector = isSuperAdmin || isDirector;
  console.log("isSuperAdmin",isSuperAdmin, "isDirector", isDirector, "isSuperAdminOrDirector", isSuperAdminOrDirector,"admin?.role",admin?.role);

  // Check if user has access to a section
  const hasAccessToSection = (sectionName) => {
    if (!admin) return false;
    const allowedSections = getDashboardItems(admin.role).map(item => item.label);
    return allowedSections.includes(sectionName);
  };

  // Redirect to Dashboard if user doesn't have access to current section
  useEffect(() => {
    if (admin && active !== 'Dashboard' && !hasAccessToSection(active)) {
      setActive('Dashboard');
    }
  }, [admin, active]);
  // Fetch admins
  useEffect(() => {
    if (admin && active === 'Admins') {
      fetchAdmins();
    }
    // eslint-disable-next-line
  }, [admin, active]);

  // Add a helper function to handle API errors and auto-logout
  const handleApiError = (err, setError) => {
    if (err.message === 'Invalid token') {
      handleLogout();
      return;
    }
    if (setError) setError(err.message);
  };

  const fetchAdmins = async (page = 1, opts = {}) => {
    setLoadingAdmins(true);
    setFormError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      const effectiveSearch = Object.prototype.hasOwnProperty.call(opts, 'search') ? opts.search : adminSearch;
      const effectiveRole = Object.prototype.hasOwnProperty.call(opts, 'role') ? opts.role : adminRoleFilter;
      if (effectiveSearch && effectiveSearch.trim()) params.set('search', effectiveSearch.trim());
      if (effectiveRole) params.set('role', effectiveRole);
      const res = await fetch(`${API_BASE_URL}/api/admins?${params.toString()}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || "You do not have permission to view this section.");
      }
      if (data.admins && Array.isArray(data.admins)) {
        setAdmins(data.admins);
        setAdminPagination(data.pagination);
      } else {
        setAdmins([]);
        setAdminPagination(null);
        setFormError(data.message || "You do not have permission to view this section.");
      }
    } catch (e) {
      if (e.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFormError('Failed to load admins');
      setAdmins([]);
      setAdminPagination(null);
    }
    setLoadingAdmins(false);
  };

  const handleAdminPageChange = (newPage) => {
    fetchAdmins(newPage);
  };

  const handleAdminSearch = () => {
    fetchAdmins(1);
  };

  const validateAdminForm = (form, admins, isEdit = false) => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errors.email = 'Invalid email';
    else if (admins.some(a => a.email === form.email && (!isEdit || a._id !== form._id))) errors.email = 'Email already exists';
    if (!isEdit && !form.password) errors.password = 'Password is required';
    if (!form.role) errors.role = 'Role is required';
    return errors;
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormTouched({ name: true, email: true, password: true, role: true });
    const errors = validateAdminForm(form, admins);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to add admin');
      }
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: '' });
      fetchAdmins();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFormError(err.message);
    }
  };

  const handleEditAdmin = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormTouched({ name: true, email: true, role: true });
    const errors = validateAdminForm(form, admins, true);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/${showEdit._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update admin');
      }
      setShowEdit(null);
      setForm({ name: '', email: '', password: '', role: '' });
      fetchAdmins();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFormError(err.message);
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    setFormError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to delete admin');
      }
      fetchAdmins();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFormError(err.message);
    }
  };

  const openEdit = (admin) => {
    setShowEdit(admin);
    setForm({ _id: admin._id, name: admin.name, email: admin.email, mobile: admin.mobile || '', role: admin.role || '', password: '' });
    setFormError('');
  };

  const closeModals = () => {
    setShowAdd(false);
    setShowEdit(null);
    setForm({ name: '', email: '', password: '', mobile: '', role: '' });
    setFormError('');
  };

  const handleFieldBlur = (field) => setFormTouched(t => ({ ...t, [field]: true }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      console.log('Attempting login to:', `${API_BASE_URL}/api/admin/login`);
      const res = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      console.log('Response status:', res.status);
      console.log('Response headers:', res.headers);
      
      // Check if response is HTML (error page)
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        const htmlText = await res.text();
        console.error('Server returned HTML instead of JSON:', htmlText);
        throw new Error('Server returned HTML page instead of JSON. Check if API server is running and URL is correct.');
      }
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed');
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('admin', JSON.stringify(data.admin));
      setAdmin(data.admin);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    setAdmin(null);
    setEmail('');
    setPassword('');
  };

  // Profile functions
  const handleProfile = () => {
    setProfileForm({
      name: admin.name,
      email: admin.email,
      mobile: admin.mobile || ''
    });
    setProfileError('');
    setShowProfileEdit(true);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update profile');
      }
      // Update local admin data
      const updatedAdmin = { ...admin, ...profileForm };
      setAdmin(updatedAdmin);
      localStorage.setItem('admin', JSON.stringify(updatedAdmin));
      setShowProfileEdit(false);
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setProfileError(err.message);
    }
    setProfileLoading(false);
  };

  const handleChangePassword = () => {
    setPasswordError('');
    setShowChangePassword(true);
  };

  const handlePasswordChange = async (passwordData) => {
    setPasswordError('');
    setPasswordLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(passwordData)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to change password');
      }
      setShowChangePassword(false);
      alert('Password changed successfully!');
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setPasswordError(err.message);
    }
    setPasswordLoading(false);
  };

  // Logo functions
  const fetchCurrentLogo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/logo`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentLogo(data.logoUrl);
        if (data.logoUrl) {
          updateFavicon(`${API_BASE_URL}${data.logoUrl}`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch logo:', err);
    }
  };

  const handleLogoUpdate = async (file) => {
    setLogoError('');
    setLogoLoading(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('logo', file);
      }

      const res = await fetch(`${API_BASE_URL}/api/logo/upload`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update logo');
      }

      setCurrentLogo(data.logoUrl);
      if (data.logoUrl) {
        updateFavicon(`${API_BASE_URL}${data.logoUrl}`);
      }
      alert('Logo updated successfully!');
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setLogoError(err.message);
    }
    setLogoLoading(false);
  };

  const handleLogoRemove = async () => {
    setLogoError('');
    setLogoLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/logo`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to remove logo');
      }

      setCurrentLogo(null);
      // revert to default app favicon
      updateFavicon('/favicon.ico');
      alert('Logo removed successfully!');
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setLogoError(err.message);
    }
    setLogoLoading(false);
  };

  // Students state
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentPagination, setStudentPagination] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentClassFilter, setStudentClassFilter] = useState('');
  const [studentSectionFilter, setStudentSectionFilter] = useState('');
  const [showStudentAdd, setShowStudentAdd] = useState(false);
  const [showStudentEdit, setShowStudentEdit] = useState(null); // student object
  const [studentForm, setStudentForm] = useState({ name: '', dob: '', gender: '', class: '', section: '', rollNumber: '', guardianName: '', guardianPhone: '', admissionDate: '' });
  const [studentFormError, setStudentFormError] = useState('');

  // Add these arrays at the top of the component (after useState declarations):
  const classOptions = [
    'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'
  ];
  const sectionOptions = ['A', 'B', 'C', 'D', 'E'];

  // Fetch students
  useEffect(() => {
    if (admin && active === 'Students') {
      fetchStudents();
    }
    // eslint-disable-next-line
  }, [admin, active]);
  const fetchStudents = async (page = 1, opts = {}) => {
    setLoadingStudents(true);
    setStudentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      const effectiveSearch = Object.prototype.hasOwnProperty.call(opts, 'search') ? opts.search : studentSearch;
      const effectiveClass = Object.prototype.hasOwnProperty.call(opts, 'class') ? opts.class : studentClassFilter;
      const effectiveSection = Object.prototype.hasOwnProperty.call(opts, 'section') ? opts.section : studentSectionFilter;
      if (effectiveSearch && effectiveSearch.trim()) params.set('search', effectiveSearch.trim());
      if (effectiveClass) params.set('class', effectiveClass);
      if (effectiveSection) params.set('section', effectiveSection);
      const res = await fetch(`${API_BASE_URL}/api/students?${params.toString()}`, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to load students');
      }
      if (data.students && Array.isArray(data.students)) {
        setStudents(data.students);
        setStudentPagination(data.pagination);
      } else {
        setStudents([]);
        setStudentPagination(null);
        setStudentFormError(data.message || 'Failed to load students');
      }
    } catch (e) {
      if (e.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setStudentFormError('Failed to load students');
      setStudents([]);
      setStudentPagination(null);
    }
    setLoadingStudents(false);
  };

  const handleStudentPageChange = (newPage) => {
    fetchStudents(newPage);
  };
  const handleStudentSearch = () => {
    fetchStudents(1);
  };

  const handleStudentSearchAction = () => {
    fetchStudents(1);
  };
  const handleStudentAdd = async (e) => {
    e.preventDefault();
    setStudentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(studentForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to add student');
      }
      setShowStudentAdd(false);
      setStudentForm({ name: '', dob: '', gender: '', class: '', section: '', rollNumber: '', guardianName: '', guardianPhone: '', admissionDate: '' });
      fetchStudents();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setStudentFormError(err.message);
    }
  };
  const handleStudentEdit = async (e) => {
    e.preventDefault();
    setStudentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/students/${showStudentEdit._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(studentForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update student');
      }
      setShowStudentEdit(null);
      setStudentForm({ name: '', dob: '', gender: '', class: '', section: '', rollNumber: '', guardianName: '', guardianPhone: '', admissionDate: '' });
      fetchStudents();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setStudentFormError(err.message);
    }
  };
  const handleStudentDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    setStudentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/students/${id}`, { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + token } });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to delete student');
      }
      fetchStudents();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setStudentFormError(err.message);
    }
  };
  const openStudentEdit = (student) => {
    setShowStudentEdit(student);
    setStudentForm({ ...student, dob: student.dob ? student.dob.slice(0, 10) : '', admissionDate: student.admissionDate ? student.admissionDate.slice(0, 10) : '' });
    setStudentFormError('');
  };
  const closeStudentModals = () => {
    setShowStudentAdd(false);
    setShowStudentEdit(null);
    setStudentForm({ name: '', dob: '', gender: '', class: '', section: '', rollNumber: '', guardianName: '', guardianPhone: '', admissionDate: '' });
    setStudentFormError('');
  };


  // Fees state
  const [fees, setFees] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [showFeeAdd, setShowFeeAdd] = useState(false);
  const [showFeeEdit, setShowFeeEdit] = useState(null); // fee object
  const [feeForm, setFeeForm] = useState({ student: '', class: '', section: '', amount: '', dueDate: '', description: '' });
  const [feeFormError, setFeeFormError] = useState('');

  // Payments state
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showPaymentAdd, setShowPaymentAdd] = useState(false);
  const [showPaymentEdit, setShowPaymentEdit] = useState(null); // payment object
  const [paymentForm, setPaymentForm] = useState({ student: '', fee: '', amount: '', date: '', method: '', receiptNumber: '' });
  const [paymentFormError, setPaymentFormError] = useState('');
  const [paymentPagination, setPaymentPagination] = useState(null);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentStudentFilter, setPaymentStudentFilter] = useState('');
  const [paymentClassFilter, setPaymentClassFilter] = useState('');
  const [paymentSectionFilter, setPaymentSectionFilter] = useState('');

  // Attendance state
  const [attendances, setAttendances] = useState([]);
  const [loadingAttendances, setLoadingAttendances] = useState(false);
  const [showAttendanceAdd, setShowAttendanceAdd] = useState(false);
  const [showAttendanceEdit, setShowAttendanceEdit] = useState(null); // attendance object
  const [attendanceForm, setAttendanceForm] = useState({ student: '', date: '', status: '', remarks: '' });
  const [attendanceFormError, setAttendanceFormError] = useState('');
  const [attendancePagination, setAttendancePagination] = useState(null);
  const [attendanceSearch, setAttendanceSearch] = useState('');
  const [attendanceStudentFilter, setAttendanceStudentFilter] = useState('');
  const [attendanceClassFilter, setAttendanceClassFilter] = useState('');
  const [attendanceSectionFilter, setAttendanceSectionFilter] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('');
  const [attendanceDateFrom, setAttendanceDateFrom] = useState('');
  const [attendanceDateTo, setAttendanceDateTo] = useState('');

  // Add these arrays at the top of the component (after useState declarations):
  const subjectOptions = ['Math', 'Science', 'English', 'History', 'Geography', 'Physics', 'Chemistry', 'Biology'];
  const genderOptions = ['Male', 'Female', 'Other'];

  // Fees filters and pagination
  const [feesPagination, setFeesPagination] = useState(null);
  const [feeSearch, setFeeSearch] = useState('');
  const [feeStudentFilter, setFeeStudentFilter] = useState('');
  const [feeClassFilter, setFeeClassFilter] = useState('');
  const [feeSectionFilter, setFeeSectionFilter] = useState('');

  // Fetch teachers, fees, and payments
  useEffect(() => {
    if (admin && active === 'Fees') {
      fetchFees(1);
      fetchStudents();
    }
    if (admin && active === 'Payments') {
      fetchPayments(1);
      fetchStudents();
      fetchFees();
    }
    if (admin && active === 'Attendance') {
      fetchAttendances(1);
      fetchStudents();
    }
    // eslint-disable-next-line
  }, [admin, active]);

  const fetchFees = async (page = 1, overrides = {}) => {
    setLoadingFees(true);
    setFeeFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      const currentSearch = Object.prototype.hasOwnProperty.call(overrides, 'search') ? overrides.search : feeSearch;
      const currentStudent = Object.prototype.hasOwnProperty.call(overrides, 'studentId') ? overrides.studentId : feeStudentFilter;
      const currentClass = Object.prototype.hasOwnProperty.call(overrides, 'class') ? overrides.class : feeClassFilter;
      const currentSection = Object.prototype.hasOwnProperty.call(overrides, 'section') ? overrides.section : feeSectionFilter;
      if (currentSearch && currentSearch.trim()) params.set('search', currentSearch.trim());
      if (currentStudent) params.set('studentId', currentStudent);
      if (currentClass) params.set('class', currentClass);
      if (currentSection) params.set('section', currentSection);
      const res = await fetch(`${API_BASE_URL}/api/fees?${params.toString()}`, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to load fees');
      }
      if (data && Array.isArray(data.fees)) {
        setFees(data.fees);
        setFeesPagination(data.pagination || null);
      } else {
      setFees(Array.isArray(data) ? data : []);
        setFeesPagination(null);
      }
    } catch (e) {
      if (e.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFeeFormError('Failed to load fees');
    }
    setLoadingFees(false);
  };

  const handleFeePageChange = (newPage) => {
    fetchFees(newPage);
  };

  const handleFeeSearchAction = () => {
    fetchFees(1);
  };

  const fetchPayments = async (page = 1, overrides = {}) => {
    setLoadingPayments(true);
    setPaymentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      const currentSearch = Object.prototype.hasOwnProperty.call(overrides, 'search') ? overrides.search : paymentSearch;
      const currentStudent = Object.prototype.hasOwnProperty.call(overrides, 'studentId') ? overrides.studentId : paymentStudentFilter;
      const currentClass = Object.prototype.hasOwnProperty.call(overrides, 'class') ? overrides.class : paymentClassFilter;
      const currentSection = Object.prototype.hasOwnProperty.call(overrides, 'section') ? overrides.section : paymentSectionFilter;
      if (currentSearch && currentSearch.trim()) params.set('search', currentSearch.trim());
      if (currentStudent) params.set('studentId', currentStudent);
      if (currentClass) params.set('class', currentClass);
      if (currentSection) params.set('section', currentSection);
      const res = await fetch(`${API_BASE_URL}/api/payments?${params.toString()}`, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to load payments');
      }
      if (data && Array.isArray(data.payments)) {
        setPayments(data.payments);
        setPaymentPagination(data.pagination || null);
      } else {
      setPayments(Array.isArray(data) ? data : []);
        setPaymentPagination(null);
      }
    } catch (e) {
      if (e.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setPaymentFormError('Failed to load payments');
    }
    setLoadingPayments(false);
  };

  const handlePaymentPageChange = (newPage) => {
    fetchPayments(newPage);
  };

  const handlePaymentSearchAction = () => {
    fetchPayments(1);
  };


  const handleFeeAdd = async (e) => {
    e.preventDefault();
    setFeeFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/fees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(feeForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to add fee');
      }
      setShowFeeAdd(false);
      setFeeForm({ student: '', class: '', section: '', amount: '', dueDate: '', description: '' });
      fetchFees();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFeeFormError(err.message);
    }
  };
  const handleFeeEdit = async (e) => {
    e.preventDefault();
    setFeeFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/fees/${showFeeEdit._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(feeForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update fee');
      }
      setShowFeeEdit(null);
      setFeeForm({ student: '', class: '', section: '', amount: '', dueDate: '', description: '' });
      fetchFees();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFeeFormError(err.message);
    }
  };
  const handleFeeDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fee?')) return;
    setFeeFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/fees/${id}`, { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + token } });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to delete fee');
      }
      fetchFees();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setFeeFormError(err.message);
    }
  };
  const openFeeEdit = (fee) => {
    setShowFeeEdit(fee);
    setFeeForm({ 
      ...fee, 
      student: fee.student ? fee.student._id : '', 
      dueDate: fee.dueDate ? fee.dueDate.slice(0, 10) : '' 
    });
    setFeeFormError('');
  };
  const closeFeeModals = () => {
    setShowFeeAdd(false);
    setShowFeeEdit(null);
    setFeeForm({ student: '', class: '', section: '', amount: '', dueDate: '', description: '' });
    setFeeFormError('');
  };

  const handleFeesPrint = () => {
    const printWindow = window.open('', '_blank');
    const table = document.getElementById('fees-table');
    if (table) {
      const printContent = `
        <html>
          <head>
            <title>Fees Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              h1 { color: #333; text-align: center; }
              .header { text-align: center; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>School Fees Report</h1>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            ${table.outerHTML.replace(/<button[^>]*>.*?<\/button>/g, '')}
          </body>
        </html>
      `;
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleFeesExportPDF = () => {
    const table = document.getElementById('fees-table');
    if (table) {
      const printContent = `
        <html>
          <head>
            <title>Fees Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              h1 { color: #333; text-align: center; }
              .header { text-align: center; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>School Fees Report</h1>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            ${table.outerHTML.replace(/<button[^>]*>.*?<\/button>/g, '')}
          </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Use browser's print to PDF functionality
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  };

  const handleFeePrint = (fee) => {
    const studentName = fee.student ? fee.student.name : 'N/A';
    const dueDate = fee.dueDate ? new Date(fee.dueDate).toLocaleDateString() : 'N/A';
    
    const printContent = `
      <html>
        <head>
          <title>Fee Record - ${studentName}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background-color: #f5f5f5;
            }
            .fee-record {
              max-width: 600px;
              margin: 0 auto;
              background: white;
              padding: 30px;
              border-radius: 10px;
              box-shadow: 0 0 20px rgba(0,0,0,0.1);
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #007bff;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 5px;
            }
            .record-title {
              font-size: 18px;
              color: #333;
              margin-bottom: 10px;
            }
            .fee-info {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .info-row:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }
            .label {
              font-weight: bold;
              color: #555;
            }
            .value {
              color: #333;
            }
            .amount-section {
              background: #007bff;
              color: white;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              margin-top: 20px;
            }
            .amount-label {
              font-size: 16px;
              margin-bottom: 5px;
            }
            .amount-value {
              font-size: 28px;
              font-weight: bold;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              color: #666;
              font-size: 14px;
            }
            @media print {
              body { background-color: white; }
              .fee-record { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="fee-record">
            <div class="header">
              <div class="school-name">School Management System</div>
              <div class="record-title">Fee Record</div>
            </div>
            
            <div class="fee-info">
              <div class="info-row">
                <span class="label">Student Name:</span>
                <span class="value">${studentName}</span>
              </div>
              <div class="info-row">
                <span class="label">Class:</span>
                <span class="value">${fee.class}</span>
              </div>
              <div class="info-row">
                <span class="label">Section:</span>
                <span class="value">${fee.section || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="label">Due Date:</span>
                <span class="value">${dueDate}</span>
              </div>
              <div class="info-row">
                <span class="label">Description:</span>
                <span class="value">${fee.description || 'N/A'}</span>
              </div>
            </div>
            
            <div class="amount-section">
              <div class="amount-label">Fee Amount</div>
              <div class="amount-value">₹${fee.amount}</div>
            </div>
            
            <div class="footer">
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
              <p>This is a computer generated record.</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Use browser's print to PDF functionality
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleFeeGeneratePDF = (fee) => {
    const studentName = fee.student ? fee.student.name : 'N/A';
    const dueDate = fee.dueDate ? new Date(fee.dueDate).toLocaleDateString() : 'N/A';
    
    const pdfContent = `
      <html>
        <head>
          <title>Fee Receipt - ${studentName}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background-color: #f5f5f5;
            }
            .receipt-container {
              max-width: 600px;
              margin: 0 auto;
              background: white;
              padding: 30px;
              border-radius: 10px;
              box-shadow: 0 0 20px rgba(0,0,0,0.1);
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #007bff;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 5px;
            }
            .receipt-title {
              font-size: 18px;
              color: #333;
              margin-bottom: 10px;
            }
            .receipt-info {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .info-row:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }
            .label {
              font-weight: bold;
              color: #555;
            }
            .value {
              color: #333;
            }
            .amount-section {
              background: #007bff;
              color: white;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              margin-top: 20px;
            }
            .amount-label {
              font-size: 16px;
              margin-bottom: 5px;
            }
            .amount-value {
              font-size: 28px;
              font-weight: bold;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              color: #666;
              font-size: 14px;
            }
            @media print {
              body { background-color: white; }
              .receipt-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              <div class="school-name">School Management System</div>
              <div class="receipt-title">Fee Receipt</div>
            </div>
            
            <div class="receipt-info">
              <div class="info-row">
                <span class="label">Student Name:</span>
                <span class="value">${studentName}</span>
              </div>
              <div class="info-row">
                <span class="label">Class:</span>
                <span class="value">${fee.class}</span>
              </div>
              <div class="info-row">
                <span class="label">Section:</span>
                <span class="value">${fee.section || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="label">Due Date:</span>
                <span class="value">${dueDate}</span>
              </div>
              <div class="info-row">
                <span class="label">Description:</span>
                <span class="value">${fee.description || 'N/A'}</span>
              </div>
            </div>
            
            <div class="amount-section">
              <div class="amount-label">Total Amount</div>
              <div class="amount-value">₹${fee.amount}</div>
            </div>
            
            <div class="footer">
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
              <p>This is a computer generated receipt.</p>
            </div>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    
    // Use browser's print to PDF functionality
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handlePaymentAdd = async (e) => {
    e.preventDefault();
    setPaymentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(paymentForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to add payment');
      }
      setShowPaymentAdd(false);
      setPaymentForm({ student: '', fee: '', amount: '', date: '', method: '', receiptNumber: '' });
      fetchPayments();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setPaymentFormError(err.message);
    }
  };
  const handlePaymentEdit = async (e) => {
    e.preventDefault();
    setPaymentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/payments/${showPaymentEdit._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(paymentForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update payment');
      }
      setShowPaymentEdit(null);
      setPaymentForm({ student: '', fee: '', amount: '', date: '', method: '', receiptNumber: '' });
      fetchPayments();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setPaymentFormError(err.message);
    }
  };
  const handlePaymentDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    setPaymentFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/payments/${id}`, { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + token } });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to delete payment');
      }
      fetchPayments();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setPaymentFormError(err.message);
    }
  };
  const openPaymentEdit = (payment) => {
    setShowPaymentEdit(payment);
    setPaymentForm({ ...payment, date: payment.date ? payment.date.slice(0, 10) : '' });
    setPaymentFormError('');
  };
  const closePaymentModals = () => {
    setShowPaymentAdd(false);
    setShowPaymentEdit(null);
    setPaymentForm({ student: '', fee: '', amount: '', date: '', method: '', receiptNumber: '' });
    setPaymentFormError('');
  };

  const handlePaymentPrint = (payment) => {
    const studentName = payment.student ? payment.student.name : 'N/A';
    const paymentDate = payment.date ? new Date(payment.date).toLocaleDateString() : 'N/A';
    const feeInfo = payment.fee ? `${payment.fee.class || ''} ${payment.fee.section || ''}`.trim() : 'N/A';

    const printContent = `
      <html>
        <head>
          <title>Payment Receipt - ${studentName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #f5f5f5;
            }
            .payment-receipt {
              max-width: 600px;
              margin: 0 auto;
              background: white;
              padding: 30px;
              border-radius: 10px;
              box-shadow: 0 0 20px rgba(0,0,0,0.1);
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #007bff;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 5px;
            }
            .receipt-title {
              font-size: 18px;
              color: #333;
              margin-bottom: 10px;
            }
            .payment-info {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .info-row:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }
            .label {
              font-weight: bold;
              color: #555;
            }
            .value {
              color: #333;
            }
            .amount-section {
              background: #28a745;
              color: white;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              margin-top: 20px;
            }
            .amount-label {
              font-size: 16px;
              margin-bottom: 5px;
            }
            .amount-value {
              font-size: 28px;
              font-weight: bold;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              color: #666;
              font-size: 14px;
            }
            @media print {
              body { background-color: white; }
              .payment-receipt { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="payment-receipt">
            <div class="header">
              <div class="school-name">School Management System</div>
              <div class="receipt-title">Payment Receipt</div>
            </div>

            <div class="payment-info">
              <div class="info-row">
                <span class="label">Student Name:</span>
                <span class="value">${studentName}</span>
              </div>
              <div class="info-row">
                <span class="label">Fee Class:</span>
                <span class="value">${feeInfo}</span>
              </div>
              <div class="info-row">
                <span class="label">Payment Date:</span>
                <span class="value">${paymentDate}</span>
              </div>
              <div class="info-row">
                <span class="label">Payment Method:</span>
                <span class="value">${payment.method}</span>
              </div>
              <div class="info-row">
                <span class="label">Receipt Number:</span>
                <span class="value">${payment.receiptNumber}</span>
              </div>
            </div>

            <div class="amount-section">
              <div class="amount-label">Amount Paid</div>
              <div class="amount-value">₹${payment.amount}</div>
            </div>

            <div class="footer">
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
              <p>This is a computer generated receipt.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handlePaymentGeneratePDF = (payment) => {
    const studentName = payment.student ? payment.student.name : 'N/A';
    const paymentDate = payment.date ? new Date(payment.date).toLocaleDateString() : 'N/A';
    const feeInfo = payment.fee ? `${payment.fee.class || ''} ${payment.fee.section || ''}`.trim() : 'N/A';

    const pdfContent = `
      <html>
        <head>
          <title>Payment Receipt - ${studentName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #f5f5f5;
            }
            .receipt-container {
              max-width: 600px;
              margin: 0 auto;
              background: white;
              padding: 30px;
              border-radius: 10px;
              box-shadow: 0 0 20px rgba(0,0,0,0.1);
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #007bff;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              color: #007bff;
              margin-bottom: 5px;
            }
            .receipt-title {
              font-size: 18px;
              color: #333;
              margin-bottom: 10px;
            }
            .receipt-info {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .info-row:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }
            .label {
              font-weight: bold;
              color: #555;
            }
            .value {
              color: #333;
            }
            .amount-section {
              background: #28a745;
              color: white;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              margin-top: 20px;
            }
            .amount-label {
              font-size: 16px;
              margin-bottom: 5px;
            }
            .amount-value {
              font-size: 28px;
              font-weight: bold;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              color: #666;
              font-size: 14px;
            }
            @media print {
              body { background-color: white; }
              .receipt-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              <div class="school-name">School Management System</div>
              <div class="receipt-title">Payment Receipt</div>
            </div>

            <div class="receipt-info">
              <div class="info-row">
                <span class="label">Student Name:</span>
                <span class="value">${studentName}</span>
              </div>
              <div class="info-row">
                <span class="label">Fee Class:</span>
                <span class="value">${feeInfo}</span>
              </div>
              <div class="info-row">
                <span class="label">Payment Date:</span>
                <span class="value">${paymentDate}</span>
              </div>
              <div class="info-row">
                <span class="label">Payment Method:</span>
                <span class="value">${payment.method}</span>
              </div>
              <div class="info-row">
                <span class="label">Receipt Number:</span>
                <span class="value">${payment.receiptNumber}</span>
              </div>
            </div>

            <div class="amount-section">
              <div class="amount-label">Total Amount Paid</div>
              <div class="amount-value">₹${payment.amount}</div>
            </div>

            <div class="footer">
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
              <p>This is a computer generated receipt.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Attendance functions
  const fetchAttendances = async (page = 1, overrides = {}) => {
    setLoadingAttendances(true);
    setAttendanceFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      const currentSearch = Object.prototype.hasOwnProperty.call(overrides, 'search') ? overrides.search : attendanceSearch;
      const currentStudent = Object.prototype.hasOwnProperty.call(overrides, 'studentId') ? overrides.studentId : attendanceStudentFilter;
      const currentClass = Object.prototype.hasOwnProperty.call(overrides, 'class') ? overrides.class : attendanceClassFilter;
      const currentSection = Object.prototype.hasOwnProperty.call(overrides, 'section') ? overrides.section : attendanceSectionFilter;
      const currentStatus = Object.prototype.hasOwnProperty.call(overrides, 'status') ? overrides.status : attendanceStatusFilter;
      const currentDateFrom = Object.prototype.hasOwnProperty.call(overrides, 'dateFrom') ? overrides.dateFrom : attendanceDateFrom;
      const currentDateTo = Object.prototype.hasOwnProperty.call(overrides, 'dateTo') ? overrides.dateTo : attendanceDateTo;
      
      if (currentSearch && currentSearch.trim()) params.set('search', currentSearch.trim());
      if (currentStudent) params.set('studentId', currentStudent);
      if (currentClass) params.set('class', currentClass);
      if (currentSection) params.set('section', currentSection);
      if (currentStatus) params.set('status', currentStatus);
      if (currentDateFrom) params.set('dateFrom', currentDateFrom);
      if (currentDateTo) params.set('dateTo', currentDateTo);
      
      const res = await fetch(`${API_BASE_URL}/api/attendance?${params.toString()}`, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to load attendance');
      }
      if (data && Array.isArray(data.attendances)) {
        setAttendances(data.attendances);
        setAttendancePagination(data.pagination || null);
      } else {
        setAttendances(Array.isArray(data) ? data : []);
        setAttendancePagination(null);
      }
    } catch (e) {
      if (e.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setAttendanceFormError('Failed to load attendance');
    }
    setLoadingAttendances(false);
  };

  const handleAttendancePageChange = (newPage) => {
    fetchAttendances(newPage);
  };

  const handleAttendanceSearchAction = () => {
    fetchAttendances(1);
  };

  const handleAttendanceAdd = async (e) => {
    e.preventDefault();
    setAttendanceFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(attendanceForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to add attendance');
      }
      setShowAttendanceAdd(false);
      setAttendanceForm({ student: '', date: '', status: '', remarks: '' });
      fetchAttendances();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setAttendanceFormError(err.message);
    }
  };

  const handleAttendanceEdit = async (e) => {
    e.preventDefault();
    setAttendanceFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/attendance/${showAttendanceEdit._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(attendanceForm)
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to update attendance');
      }
      setShowAttendanceEdit(null);
      setAttendanceForm({ student: '', date: '', status: '', remarks: '' });
      fetchAttendances();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setAttendanceFormError(err.message);
    }
  };

  const handleAttendanceDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attendance record?')) return;
    setAttendanceFormError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/attendance/${id}`, { method: 'DELETE', headers: { 'Authorization': 'Bearer ' + token } });
      const data = await res.json();
      if (!res.ok) {
        if (data.message === 'Invalid token') {
          handleLogout();
          return;
        }
        throw new Error(data.message || 'Failed to delete attendance');
      }
      fetchAttendances();
    } catch (err) {
      if (err.message === 'Invalid token') {
        handleLogout();
        return;
      }
      setAttendanceFormError(err.message);
    }
  };

  const openAttendanceEdit = (attendance) => {
    setShowAttendanceEdit(attendance);
    setAttendanceForm({
      ...attendance,
      student: attendance.student ? attendance.student._id : '',
      date: attendance.date ? attendance.date.slice(0, 10) : ''
    });
    setAttendanceFormError('');
  };

  const closeAttendanceModals = () => {
    setShowAttendanceAdd(false);
    setShowAttendanceEdit(null);
    setAttendanceForm({ student: '', date: '', status: '', remarks: '' });
    setAttendanceFormError('');
  };

  // Dashboard UI
  const renderDashboard = () => (
    <div className="dashboard-main">
      <div className="dashboard-welcome">
        <span role="img" aria-label="school">🏫</span>
        Welcome, {admin.name} ({admin.role})
      </div>
      <h2 className="dashboard-title">Dashboard Overview</h2>
      <div className="dashboard-cards-grid">
        <div className="dashboard-summary-card students">
          <div className="dashboard-summary-icon">👨‍🎓</div>
          <div className="dashboard-summary-title">Total Students</div>
          <div className="dashboard-summary-value">{dashboardStats.students || 0}</div>
        </div>
        <div className="dashboard-summary-card admins">
          <div className="dashboard-summary-icon">🛡️</div>
          <div className="dashboard-summary-title">Total Admins</div>
          <div className="dashboard-summary-value">{dashboardStats.admins || 0}</div>
        </div>
        <div className="dashboard-summary-card fees">
          <div className="dashboard-summary-icon">💰</div>
          <div className="dashboard-summary-title">Total Fees Collection</div>
          <div className="dashboard-summary-value">₹{(dashboardStats.feesCollected || 0).toLocaleString()}</div>
        </div>
        <div className="dashboard-summary-card attendance">
          <div className="dashboard-summary-icon">📋</div>
          <div className="dashboard-summary-title">Today's Attendance</div>
          <div className="dashboard-summary-value">{dashboardStats.todayAttendance || 0}</div>
        </div>
        <div className="dashboard-summary-card pending">
          <div className="dashboard-summary-icon">⏰</div>
          <div className="dashboard-summary-title">Pending Fees</div>
          <div className="dashboard-summary-value">{dashboardStats.pendingFees || 0}</div>
        </div>
        <div className="dashboard-summary-card total-fees">
          <div className="dashboard-summary-icon">📊</div>
          <div className="dashboard-summary-title">Total Fees</div>
          <div className="dashboard-summary-value">{dashboardStats.totalFees || 0}</div>
        </div>
      </div>
      <div className="dashboard-recent-section">
        <h3 className="dashboard-recent-title">Recent Admissions</h3>
        <ul className="dashboard-recent-list">
          {dashboardStats.recentStudents && dashboardStats.recentStudents.length > 0 ? dashboardStats.recentStudents.map(s => (
            <li key={s._id} className="dashboard-recent-item">
              <span className="recent-name">{s.name}</span>
              <span className="recent-class">Class: {s.class}</span>
              <span className="recent-date">{s.admissionDate && new Date(s.admissionDate).toLocaleDateString()}</span>
            </li>
          )) : <li className="dashboard-recent-item">No recent admissions</li>}
        </ul>
      </div>
    </div>
  );

  const Loader = () => (
    <div className="custom-loader-container">
      <div className="custom-loader"></div>
      <div style={{marginTop:'1rem', color:'#555', fontWeight:500}}>Loading...</div>
    </div>
  );

  // Server-side filtering is used; no client-side filtering applied

  const renderAdmins = () => (
    <div className="admins-section">
          <div className="admins-header">
            <h2 style={{margin:0}}>Admins</h2>
        <button className="add-btn" onClick={() => setShowAdd(true)}>+ Add Admin</button>
      </div>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name or email"
          value={adminSearch}
          onChange={e=>setAdminSearch(e.target.value)}
          onKeyDown={e=>{ if (e.key === 'Enter') handleAdminSearch(); }}
          className="filters-input"
        />
        <select
          value={adminRoleFilter}
          onChange={e=>setAdminRoleFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key === 'Enter') handleAdminSearch(); }}
          className="filters-select"
        >
          <option value="">All Roles</option>
          {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button className="filters-search-btn" onClick={handleAdminSearch}>Search</button>
        <button className="filters-clear-btn" onClick={() => { setAdminSearch(''); setAdminRoleFilter(''); fetchAdmins(1, { search: '', role: '' }); }}>Clear</button>
          </div>
          {formError && <div className="error" style={{marginBottom:'1rem'}}>{formError}</div>}
      {loadingAdmins ? <div className="loading-state">Loading...</div> : (
          <AdminsTable
            admins={admins}
            isSuperAdmin={isSuperAdmin}
            isDirector={isDirector}
            openEdit={openEdit}
            handleDelete={handleDeleteAdmin}
          />
      )}
          {adminPagination && (
            <Pagination
              pagination={adminPagination}
              onPageChange={handleAdminPageChange}
            />
          )}
          {/* Add Modal */}
          {showAdd && (
            <div className="modal-backdrop">
              <div className="modal">
                <h3>Add Admin</h3>
                <AdminForm
                  form={form}
                  formTouched={formTouched}
                  formErrors={formErrors}
                  formError={formError}
                  roleOptions={roleOptions}
                  onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
                  onBlur={handleFieldBlur}
                  onSubmit={handleAddAdmin}
                  onCancel={closeModals}
                  isEdit={false}
                />
              </div>
            </div>
          )}
          {/* Edit Modal */}
          {showEdit && (
            <div className="modal-backdrop">
              <div className="modal">
                <h3>Edit Admin</h3>
                <AdminForm
                  form={form}
                  formTouched={formTouched}
                  formErrors={formErrors}
                  formError={formError}
                  roleOptions={roleOptions}
                  onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
                  onBlur={handleFieldBlur}
                  onSubmit={handleEditAdmin}
                  onCancel={closeModals}
                  isEdit={true}
                />
              </div>
            </div>
      )}
    </div>
  );

  const renderStudents = () => (
    <div className="admins-section">
          <div className="admins-header">
            <h2 style={{margin:0}}>Students</h2>
        <button className="add-btn" onClick={() => setShowStudentAdd(true)}>+ Add Student</button>
      </div>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search student name / roll number"
          value={studentSearch}
          onChange={e=>setStudentSearch(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleStudentSearchAction(); }}
          className="filters-input"
        />
        <select
          value={studentClassFilter}
          onChange={e=>setStudentClassFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleStudentSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Classes</option>
          {['Class 1','Class 2','Class 3','Class 4','Class 5','Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={studentSectionFilter}
          onChange={e=>setStudentSectionFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleStudentSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Sections</option>
          {['A','B','C','D','E'].map(sec => (
            <option key={sec} value={sec}>{sec}</option>
          ))}
        </select>
        <button className="filters-search-btn" onClick={handleStudentSearchAction}>Search</button>
        <button className="filters-clear-btn" onClick={()=>{ setStudentSearch(''); setStudentClassFilter(''); setStudentSectionFilter(''); fetchStudents(1, { search: '', class: '', section: '' }); }}>Clear</button>
          </div>
          {studentFormError && <div className="error" style={{marginBottom:'1rem'}}>{studentFormError}</div>}
      {loadingStudents ? <div className="loading-state">Loading...</div> : (
          <StudentsTable
            students={students}
            isSuperAdmin={isSuperAdmin}
            isDirector={isDirector}
            openEdit={openStudentEdit}
            handleDelete={handleStudentDelete}
          />
      )}
          {studentPagination && (
            <Pagination
              pagination={studentPagination}
              onPageChange={handleStudentPageChange}
            />
          )}
          {/* Add Modal */}
          {showStudentAdd && (
            <div className="modal-backdrop">
              <div className="modal">
                <h3>Add Student</h3>
                <StudentForm
                  form={studentForm}
                  formError={studentFormError}
                  classOptions={['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12']}
                  sectionOptions={['A', 'B', 'C', 'D', 'E']}
                  onChange={(field, value) => setStudentForm(f => ({ ...f, [field]: value }))}
                  onSubmit={handleStudentAdd}
                  onCancel={closeStudentModals}
                  isEdit={false}
                />
              </div>
            </div>
          )}
          {/* Edit Modal */}
          {showStudentEdit && (
            <div className="modal-backdrop">
              <div className="modal">
                <h3>Edit Student</h3>
                <StudentForm
                  form={studentForm}
                  formError={studentFormError}
                  classOptions={['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12']}
                  sectionOptions={['A', 'B', 'C', 'D', 'E']}
                  onChange={(field, value) => setStudentForm(f => ({ ...f, [field]: value }))}
                  onSubmit={handleStudentEdit}
                  onCancel={closeStudentModals}
                  isEdit={true}
                />
              </div>
            </div>
          )}
    
    </div>
  );


  const renderFees = () => (
    <div className="admins-section">
      <div className="admins-header">
        <h2 style={{margin:0}}>Fees</h2>
        <button className="add-btn" onClick={() => setShowFeeAdd(true)}>+ Add Fee</button>
      </div>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search description / class / section"
          value={feeSearch}
          onChange={e=>setFeeSearch(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleFeeSearchAction(); }}
          className="filters-input"
        />
        <select
          value={feeClassFilter}
          onChange={e=>setFeeClassFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleFeeSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Classes</option>
          {['Class 1','Class 2','Class 3','Class 4','Class 5','Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={feeSectionFilter}
          onChange={e=>setFeeSectionFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleFeeSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Sections</option>
          {['A','B','C','D','E'].map(sec => (
            <option key={sec} value={sec}>{sec}</option>
          ))}
        </select>
        <select
          value={feeStudentFilter}
          onChange={e=>setFeeStudentFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleFeeSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Students</option>
          {students && students.map(s => (
            <option key={s._id} value={s._id}>
              {s.name} - Class {s.class}{s.section ? ` (${s.section})` : ''}
            </option>
          ))}
        </select>
        <button className="filters-search-btn" onClick={handleFeeSearchAction}>Search</button>
        <button className="filters-clear-btn" onClick={() => {
          setFeeSearch('');
          setFeeStudentFilter('');
          setFeeClassFilter('');
          setFeeSectionFilter('');
          fetchFees(1, { search: '', studentId: '', class: '', section: '' });
        }}>Clear</button>
      </div>
      {feeFormError && <div className="error" style={{marginBottom:'1rem'}}>{feeFormError}</div>}
        {loadingFees ? <div className="loading-state">Loading...</div> : (
          <FeesTable
            fees={fees}
            openEdit={openFeeEdit}
            handleDelete={handleFeeDelete}
            onPrint={handleFeesPrint}
            onExportPDF={handleFeesExportPDF}
            onGeneratePDF={handleFeeGeneratePDF}
            onFeePrint={handleFeePrint}
          />
        )}
      {feesPagination && (
        <Pagination
          pagination={feesPagination}
          onPageChange={handleFeePageChange}
        />
      )}
      {/* Add Modal */}
      {showFeeAdd && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add Fee</h3>
            <FeeForm
              form={feeForm}
              formError={feeFormError}
              onChange={(field, value) => setFeeForm(f => ({ ...f, [field]: value }))}
              onSubmit={handleFeeAdd}
              onCancel={closeFeeModals}
              isEdit={false}
              students={students}
            />
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {showFeeEdit && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Edit Fee</h3>
            <FeeForm
              form={feeForm}
              formError={feeFormError}
              onChange={(field, value) => setFeeForm(f => ({ ...f, [field]: value }))}
              onSubmit={handleFeeEdit}
              onCancel={closeFeeModals}
              isEdit={true}
              students={students}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderPayments = () => (
    <div className="admins-section">
      <div className="admins-header">
        <h2 style={{margin:0}}>Payments</h2>
        <button className="add-btn" onClick={() => setShowPaymentAdd(true)}>+ Add Payment</button>
      </div>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search method / receipt"
          value={paymentSearch}
          onChange={e=>setPaymentSearch(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handlePaymentSearchAction(); }}
          className="filters-input"
        />
        <select
          value={paymentClassFilter}
          onChange={e=>setPaymentClassFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handlePaymentSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Classes</option>
          {['Class 1','Class 2','Class 3','Class 4','Class 5','Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={paymentSectionFilter}
          onChange={e=>setPaymentSectionFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handlePaymentSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Sections</option>
          {['A','B','C','D','E'].map(sec => (
            <option key={sec} value={sec}>{sec}</option>
          ))}
        </select>
        <select
          value={paymentStudentFilter}
          onChange={e=>setPaymentStudentFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handlePaymentSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Students</option>
          {students && students.map(s => (
            <option key={s._id} value={s._id}>
              {s.name} - Class {s.class}{s.section ? ` (${s.section})` : ''}
            </option>
          ))}
        </select>
        <button className="filters-search-btn" onClick={handlePaymentSearchAction}>Search</button>
        <button className="filters-clear-btn" onClick={() => {
          setPaymentSearch('');
          setPaymentStudentFilter('');
          setPaymentClassFilter('');
          setPaymentSectionFilter('');
          fetchPayments(1, { search: '', studentId: '', class: '', section: '' });
        }}>Clear</button>
      </div>
      {paymentFormError && <div className="error" style={{marginBottom:'1rem'}}>{paymentFormError}</div>}
        {loadingPayments ? <div className="loading-state">Loading...</div> : (
          <PaymentsTable
            payments={payments}
            openEdit={openPaymentEdit}
            handleDelete={handlePaymentDelete}
            onPaymentPrint={handlePaymentPrint}
            onPaymentGeneratePDF={handlePaymentGeneratePDF}
          />
        )}
      {paymentPagination && (
        <Pagination
          pagination={paymentPagination}
          onPageChange={handlePaymentPageChange}
        />
      )}
      {/* Add Modal */}
      {showPaymentAdd && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add Payment</h3>
            <PaymentForm
              form={paymentForm}
              formError={paymentFormError}
              onChange={(field, value) => setPaymentForm(f => ({ ...f, [field]: value }))}
              onSubmit={handlePaymentAdd}
              onCancel={closePaymentModals}
              isEdit={false}
              students={students}
              fees={fees}
            />
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {showPaymentEdit && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Edit Payment</h3>
            <PaymentForm
              form={paymentForm}
              formError={paymentFormError}
              onChange={(field, value) => setPaymentForm(f => ({ ...f, [field]: value }))}
              onSubmit={handlePaymentEdit}
              onCancel={closePaymentModals}
              isEdit={true}
              students={students}
              fees={fees}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderAttendance = () => (
    <div className="admins-section">
      <div className="admins-header">
        <h2 style={{margin:0}}>Attendance</h2>
        <button className="add-btn" onClick={() => setShowAttendanceAdd(true)}>+ Add Attendance</button>
      </div>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search student name / class / section / remarks"
          value={attendanceSearch}
          onChange={e=>setAttendanceSearch(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-input"
        />
        <select
          value={attendanceClassFilter}
          onChange={e=>setAttendanceClassFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Classes</option>
          {['Class 1','Class 2','Class 3','Class 4','Class 5','Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={attendanceSectionFilter}
          onChange={e=>setAttendanceSectionFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Sections</option>
          {['A','B','C','D','E'].map(sec => (
            <option key={sec} value={sec}>{sec}</option>
          ))}
        </select>
        <select
          value={attendanceStatusFilter}
          onChange={e=>setAttendanceStatusFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Status</option>
          <option value="Present">Present</option>
          <option value="Absent">Absent</option>
          <option value="Late">Late</option>
          <option value="Excused">Excused</option>
        </select>
        <input
          type="date"
          placeholder="From Date"
          value={attendanceDateFrom}
          onChange={e=>setAttendanceDateFrom(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-input"
        />
        <input
          type="date"
          placeholder="To Date"
          value={attendanceDateTo}
          onChange={e=>setAttendanceDateTo(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-input"
        />
        <select
          value={attendanceStudentFilter}
          onChange={e=>setAttendanceStudentFilter(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter') handleAttendanceSearchAction(); }}
          className="filters-select"
        >
          <option value="">All Students</option>
          {students && students.map(s => (
            <option key={s._id} value={s._id}>
              {s.name} - Class {s.class}{s.section ? ` (${s.section})` : ''}
            </option>
          ))}
        </select>
        <button className="filters-search-btn" onClick={handleAttendanceSearchAction}>Search</button>
        <button className="filters-clear-btn" onClick={() => {
          setAttendanceSearch('');
          setAttendanceStudentFilter('');
          setAttendanceClassFilter('');
          setAttendanceSectionFilter('');
          setAttendanceStatusFilter('');
          setAttendanceDateFrom('');
          setAttendanceDateTo('');
          fetchAttendances(1, { search: '', studentId: '', class: '', section: '', status: '', dateFrom: '', dateTo: '' });
        }}>Clear</button>
      </div>
      {attendanceFormError && <div className="error" style={{marginBottom:'1rem'}}>{attendanceFormError}</div>}
      {loadingAttendances ? <div className="loading-state">Loading...</div> : (
        <AttendanceTable
          attendances={attendances}
          openEdit={openAttendanceEdit}
          handleDelete={handleAttendanceDelete}
        />
      )}
      {attendancePagination && (
        <Pagination
          pagination={attendancePagination}
          onPageChange={handleAttendancePageChange}
        />
      )}
      {/* Add Modal */}
      {showAttendanceAdd && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add Attendance</h3>
            <AttendanceForm
              form={attendanceForm}
              formError={attendanceFormError}
              onChange={(field, value) => setAttendanceForm(f => ({ ...f, [field]: value }))}
              onSubmit={handleAttendanceAdd}
              onCancel={closeAttendanceModals}
              isEdit={false}
              students={students}
            />
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {showAttendanceEdit && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Edit Attendance</h3>
            <AttendanceForm
              form={attendanceForm}
              formError={attendanceFormError}
              onChange={(field, value) => setAttendanceForm(f => ({ ...f, [field]: value }))}
              onSubmit={handleAttendanceEdit}
              onCancel={closeAttendanceModals}
              isEdit={true}
              students={students}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderProfile = () => (
    <div className="admins-section">
      <div className="admins-header">
        <h2 style={{margin:0}}>My Profile</h2>
      </div>
      <div className="profile-content">
        <div className="profile-info">
          <div className="profile-field">
            <label>Name:</label>
            <span>{admin.name}</span>
          </div>
          <div className="profile-field">
            <label>Email:</label>
            <span>{admin.email}</span>
          </div>
          <div className="profile-field">
            <label>Mobile:</label>
            <span>{admin.mobile || 'N/A'}</span>
          </div>
          <div className="profile-field">
            <label>Role:</label>
            <span>{admin.role}</span>
          </div>
        </div>
        <div className="profile-actions">
          <button className="edit-btn" onClick={handleProfile}>
            Edit Profile
          </button>
          <button className="change-password-btn" onClick={handleChangePassword}>
            Change Password
          </button>
        </div>
      </div>
    </div>
  );

  const renderLogo = () => (
    <div className="admins-section">
      <div className="admins-header">
        <h2 style={{margin:0}}>Logo Settings</h2>
      </div>
      <LogoManager
        currentLogo={currentLogo}
        onLogoUpdate={handleLogoUpdate}
        loading={logoLoading}
        error={logoError}
        adminRole={admin?.role}
      />
    </div>
  );

  if (admin) {
    console.log('Logged in as:', admin && admin.role);
    return (
      <div className={`dashboard-layout${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
        <aside className={`sidebar${sidebarOpen ? '' : ' collapsed'}`}>
          <div className="sidebar-toggle" onClick={() => setSidebarOpen(o => !o)}>
            {sidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
          </div>
          <div className="sidebar-logo">
            {currentLogo ? (
              <img src={`${API_BASE_URL}${currentLogo}`} alt="School Logo" className="sidebar-logo-image" />
            ) : (
              <>
            <span role="img" aria-label="school">🏫</span>
            {sidebarOpen && <span className="sidebar-title">SchoolMS</span>}
              </>
            )}
          </div>
          <nav className="sidebar-nav">
            {getDashboardItems(admin?.role).map(item => (
              <div
                key={item.label}
                className={`sidebar-link${active === item.label ? ' active' : ''}`}
                onClick={() => setActive(item.label)}
                title={item.label}
              >
                <span className="sidebar-icon" role="img" aria-label={item.label}>{item.icon}</span>
                {sidebarOpen && <span>{item.label}</span>}
              </div>
            ))}
          </nav>
        </aside>
        <main className="main-content">
          <header className="top-header">
            <div className="top-left">
              <button className="mobile-menu-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle Menu">☰</button>
            <span className="top-title">School Management System</span>
            </div>
            <div className="top-user" style={{position:'relative'}}>
              <FaUserCircle className="profile-icon" onClick={() => setProfileOpen(o => !o)} style={{cursor:'pointer', fontSize:'2rem'}} />
              {profileOpen && (
                <div className="profile-dropdown" onMouseLeave={() => setProfileOpen(false)}>
                  <div className="profile-dropdown-info">
                    <div className="profile-dropdown-name">{admin.name}</div>
                    <div className="profile-dropdown-role">{admin.role}</div>
                  </div>
                  <button className="profile-dropdown-action" onClick={() => { setProfileOpen(false); handleProfile(); }}>My Profile</button>
                  <button className="profile-dropdown-action" onClick={() => { setProfileOpen(false); handleChangePassword(); }}>Change Password</button>
                  <button className="profile-dropdown-logout" onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          </header>
          <div className="main-inner">
            {active === 'Dashboard' ? renderDashboard() :
              active === 'Admins' && hasAccessToSection('Admins') ? renderAdmins() :
              active === 'Students' && hasAccessToSection('Students') ? renderStudents() :
              active === 'Fees' && hasAccessToSection('Fees') ? renderFees() :
              active === 'Payments' && hasAccessToSection('Payments') ? renderPayments() :
              active === 'Attendance' && hasAccessToSection('Attendance') ? renderAttendance() :
              active === 'Logo Settings' && hasAccessToSection('Logo Settings') ? renderLogo() :
              active === 'My Profile' && hasAccessToSection('My Profile') ? renderProfile() : (
                <>
                  <h2>{active}</h2>
                  <div className="dashboard-cards">
                    <div style={{color:'#888',marginTop:'2rem'}}>Select a section from the sidebar to manage.</div>
                  </div>
                </>
              )}
          </div>
        </main>
        {/* Global Modals: Profile and Change Password */}
        {showProfileEdit && (
          <div className="modal-backdrop">
            <div className="modal">
              <h3>Edit Profile</h3>
              <ProfileForm
                admin={admin}
                form={profileForm}
                setForm={setProfileForm}
                onSubmit={handleProfileUpdate}
                onCancel={() => setShowProfileEdit(false)}
                error={profileError}
                loading={profileLoading}
              />
            </div>
          </div>
        )}
        {showChangePassword && (
          <div className="modal-backdrop">
            <div className="modal">
              <h3>Change Password</h3>
              <ChangePasswordForm
                onSubmit={handlePasswordChange}
                onCancel={() => setShowChangePassword(false)}
                error={passwordError}
                loading={passwordLoading}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="App">
        <div className="school-header">
          <span className="icon" role="img" aria-label="school">🏫</span>
          School Management System
        </div>
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="email"
            placeholder="Admin Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          <button type="submit">Login</button>
          {error && <div className="error">{error}</div>}
        </form>
      </div>
    );
  }
}

export default App;
